import { CalendarDate, getLocalTimeZone, today } from '@internationalized/date';
import { defineStore } from 'pinia';
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';

// Define our own DateRange type with non-nullable dates
interface DateRangeValue {
  start: CalendarDate;
  end: CalendarDate;
}

/**
 * Date Range Store - Centralized date management for Dashboard and Calls pages
 *
 * Architecture:
 * - URL query parameters are the source of truth (?startDate=X&endDate=Y)
 * - Store provides computed helpers derived from URL
 * - Store actions update URL via router.push()
 * - No persistence in localStorage (URL is already persistent)
 */

/**
 * Helper function to safely parse date string to CalendarDate
 * Returns fallback date if parsing fails
 *
 * Based on existing pattern from pages/calls/index.vue lines 248-260
 */
function safeParseDate(dateStr: string | undefined, fallback: CalendarDate): CalendarDate {
  if (!dateStr) {return fallback;}

  try {
    const [year, month, day] = dateStr.split('-').map(Number);
    if (!year || !month || !day) {return fallback;}

    const date = new CalendarDate(year, month, day);

    // Validate date is not in the future
    const dateMs = new Date(year, month - 1, day, 12, 0, 0).getTime();
    const todayDate = today(getLocalTimeZone());
    const todayMs = new Date(
      todayDate.year,
      todayDate.month - 1,
      todayDate.day,
      12,
      0,
      0,
    ).getTime();

    if (dateMs > todayMs) {return fallback;}

    return date;
  } catch (error) {
    console.warn(`Invalid date string in URL: "${dateStr}". Falling back to default.`, error);
    return fallback;
  }
}

/**
 * Helper to convert CalendarDate to YYYY-MM-DD string
 */
function calendarDateToString(date: CalendarDate): string {
  return `${date.year}-${String(date.month).padStart(2, '0')}-${String(date.day).padStart(2, '0')}`;
}

export const useDateRangeStore = defineStore('date-range', () => {
  const route = useRoute();
  const router = useRouter();

  // Default to today if no URL params
  const defaultDate = computed(() => today(getLocalTimeZone()));

  /**
   * Selected date range - computed from URL query parameters
   * This is the source of truth for the current date selection
   */
  const selectedDateRange = computed<DateRangeValue>(() => {
    const startStr = route.query.startDate as string | undefined;
    const endStr = route.query.endDate as string | undefined;

    const end = safeParseDate(endStr, defaultDate.value);
    const start = safeParseDate(startStr, defaultDate.value);

    return { start, end };
  });

  /**
   * Check if the selected range is a single day
   */
  const isSingleDay = computed(() => {
    const { start, end } = selectedDateRange.value;
    return start.compare(end) === 0;
  });

  /**
   * Formatted date range for display
   * Single day: "Oct 20, 2025"
   * Range: "Oct 10 - Oct 20, 2025"
   */
  const formattedDateRange = computed(() => {
    const { start, end } = selectedDateRange.value;

    const startDate = new Date(start.year, start.month - 1, start.day);
    const endDate = new Date(end.year, end.month - 1, end.day);

    const formatOptions: Intl.DateTimeFormatOptions = {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    };

    if (isSingleDay.value) {
      return startDate.toLocaleDateString('en-US', formatOptions);
    }

    const startFormatted = startDate.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
    const endFormatted = endDate.toLocaleDateString('en-US', formatOptions);

    return `${startFormatted} - ${endFormatted}`;
  });

  /**
   * Get date strings in YYYY-MM-DD format for API calls
   */
  const startDateString = computed(() => calendarDateToString(selectedDateRange.value.start));
  const endDateString = computed(() => calendarDateToString(selectedDateRange.value.end));

  /**
   * Get effective date range based on context
   *
   * For Trends tab: Expands single day to 7-day range
   * For other contexts: Returns selected range as-is
   *
   * @param context - The context requesting the date range
   * @returns Object with start and end date strings
   */
  function getEffectiveDateRange(context: 'analytics' | 'trends' | 'staff' | 'calls'): {
    start: string;
    end: string;
  } {
    const { start, end } = selectedDateRange.value;

    // Trends tab: expand to 7 days if single day selected
    if (context === 'trends' && isSingleDay.value) {
      const expandedStart = start.subtract({ days: 6 }); // 7 days total
      return {
        start: calendarDateToString(expandedStart),
        end: calendarDateToString(end),
      };
    }

    // All other contexts: use selected range as-is
    return {
      start: calendarDateToString(start),
      end: calendarDateToString(end),
    };
  }

  /**
   * Set a new date range and update URL
   * This is the primary way to change the selected date range
   *
   * @param start - Start date
   * @param end - End date
   */
  function setDateRange(start: CalendarDate, end: CalendarDate) {
    // Validate: end must not be before start
    if (end.compare(start) < 0) {
      console.error('Invalid date range: end date is before start date');
      return;
    }

    // Validate: cannot be in future
    const todayValue = today(getLocalTimeZone());
    if (start.compare(todayValue) > 0) {
      console.error('Invalid date range: start date is in the future');
      return;
    }

    // Explicitly exclude ALL date-related params to prevent pollution
    // Only preserve non-date query params (like filters, search, etc.)
    const { date: _date, startDate: _startDate, endDate: _endDate, ...otherQuery } = route.query;

    router.push({
      query: {
        ...otherQuery, // Only non-date params
        startDate: calendarDateToString(start),
        endDate: calendarDateToString(end),
      },
    });
  }

  /**
   * Shift the current date range by a number of days
   * Useful for "previous/next" navigation buttons
   *
   * @param days - Number of days to shift (positive for future, negative for past)
   */
  function shiftDateRange(days: number) {
    const { start, end } = selectedDateRange.value;
    const newStart = start.add({ days });
    const newEnd = end.add({ days });
    setDateRange(newStart, newEnd);
  }

  /**
   * Navigate to previous date range
   * Single day: shifts by 1 day
   * Range: shifts by range length
   */
  function goToPrevious() {
    const { start, end } = selectedDateRange.value;
    const rangeDays = Math.abs(end.compare(start)) + 1;
    shiftDateRange(-rangeDays);
  }

  /**
   * Navigate to next date range
   * Single day: shifts by 1 day
   * Range: shifts by range length
   */
  function goToNext() {
    const { start, end } = selectedDateRange.value;
    // Prevent navigating into the future
    const tomorrow = today(getLocalTimeZone()).add({ days: 1 });
    if (end.compare(tomorrow) >= 0) {return;}

    const rangeDays = Math.abs(end.compare(start)) + 1;
    shiftDateRange(rangeDays);
  }

  return {
    // State (computed from URL)
    selectedDateRange,
    isSingleDay,
    formattedDateRange,
    startDateString,
    endDateString,

    // Helpers
    getEffectiveDateRange,

    // Actions
    setDateRange,
    shiftDateRange,
    goToPrevious,
    goToNext,
  };
});
